import { useCallback, useEffect, useMemo, useState } from "react";
import { formatHours } from "../components/utils/formatters";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import FancyPaper from "../components/FancyPaper";
import { useNavigate } from "react-router-dom";
import { useApp } from "../hooks/useApp";
import {
  AdministrativePerson,
  AdministrativeSalaryCalculation,
  AdministrativeSalaryDay,
  getAdministrativePersons,
  getAdministrativeSalaryCalculation,
  saveAdministrativeSalary,
} from "../services/administrativeSalary/AdministrativeSalaryService";

const MONTH_LABELS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

/** Azul de los días de vacaciones, el mismo que usa la grilla de Avances. */
const VACATION_COLOR = "#0288d1";

const WEEK_DAY_LABELS = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

// Semanas Jue→Mié dentro del mes:
//   - El día 1 siempre cae en la semana 1 (aunque sea fragmento del bloque
//     anterior, p.ej. un único miércoles).
//   - Cada jueves posterior al día 1 abre una semana nueva (semana 2, 3, …).
// Ejemplos:
//   Abr 2026: Apr 1=Mié → S1 = [Apr 1]; Apr 2=Jue → S2 arranca; …
//   May 2026: May 1=Vie → S1 = [May 1..May 6]; May 7=Jue → S2 arranca; …
const countWeeksInMonth = (year: number, month: number): number => {
  const lastDay = new Date(year, month, 0).getDate();
  let count = 1;
  for (let d = 2; d <= lastDay; d++) {
    if (new Date(year, month - 1, d).getDay() === 4) count++;
  }
  return count;
};

const formatCRC = (value: number) =>
  new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);

const buildMonthOptions = () => {
  const today = new Date();
  const opts: { value: string; label: string; year: number; month: number }[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    opts.push({
      value: `${y}-${String(m).padStart(2, "0")}`,
      label: `${MONTH_LABELS[m - 1]} ${y}`,
      year: y,
      month: m,
    });
  }
  return opts;
};

// Posición de semana (1-based) dentro del mes propio del dateStr, usando el
// mismo criterio Jue→Mié: día 1 = semana 1; cada jueves dentro del mes incrementa.
const getWeekPositionInMonth = (dateStr: string): number => {
  const [y, m, d] = dateStr.split("-").map(Number);
  let week = 1;
  for (let i = 2; i <= d; i++) {
    if (new Date(y, m - 1, i).getDay() === 4) week++;
  }
  return week;
};

// Estilos compartidos para coincidir con el prototipo
const cellHeadSx = {
  color: "#222",
  fontWeight: 700,
  fontSize: 14,
  borderBottom: "1px solid #E5E5E5",
  py: 1.5,
};

// Input ovalado/píldora con borde sutil
const PillInput = ({
  value,
  onChange,
  disabled,
  error,
}: {
  value: any;
  onChange: (v: string) => void;
  disabled?: boolean;
  error?: boolean;
}) => {
  const [text, setText] = useState(String(value ?? 0));

  useEffect(() => {
    setText(String(value ?? 0));
  }, [value]);

  const commit = () => {
    if (text === "" || text === "-") {
      onChange("0");
      setText("0");
      return;
    }
    let num = Number(text);
    if (Number.isNaN(num) || num < 0) {
      onChange("0");
      setText("0");
      return;
    }
    if (num > 24) num = 24;
    // Redondeo a cuartos de hora (0.25); antes forzaba a 0.5 (Math.ceil(num*2)/2)
    const rounded = Math.round(num * 4) / 4;
    const out = String(rounded);
    onChange(out);
    setText(out);
  };

  return (
    <TextField
      type="number"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      disabled={disabled}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
      }}
      inputProps={{
        min: 0,
        max: 24,
        step: 0.5,
        style: { textAlign: "center", padding: "3px 12px" },
      }}
      sx={{
        width: 280,
        maxWidth: "100%",
        "& .MuiOutlinedInput-root": {
          borderRadius: "12px !important",
          background: error ? "rgba(211, 47, 47, 0.08)" : "#FFF",
          "& fieldset": {
            borderColor: error ? "#D32F2F" : "#D9D9D9",
            borderWidth: error ? 2 : 1,
            borderRadius: "12px !important",
          },
          "&:hover fieldset": { borderColor: error ? "#D32F2F" : "#BDBDBD" },
          "&.Mui-focused fieldset": { borderColor: error ? "#D32F2F" : undefined },
          "&.Mui-disabled": {
            background: error ? "rgba(211, 47, 47, 0.08)" : undefined,
            "& fieldset": {
              borderColor: error ? "#D32F2F" : "#D9D9D9",
              borderWidth: error ? 2 : 1,
            },
          },
        },
        "& .MuiOutlinedInput-input": {
          background: "transparent !important",
          fontWeight: error ? 700 : undefined,
          color: error ? "#D32F2F" : undefined,
        },
        "& .MuiInputBase-input.Mui-disabled": {
          WebkitTextFillColor: error ? "#D32F2F" : undefined,
          fontWeight: error ? 700 : undefined,
        },
        "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button": {
          WebkitAppearance: "none",
          margin: 0,
        },
        "& input[type=number]": { MozAppearance: "textfield" },
      }}
    />
  );
};

const ManagementSalaryCalculation = () => {
  const navigate = useNavigate();
  const { authInfo, setLoading, setErrorMsg, setSuccessMsg } = useApp() as any;

  const monthOptions = useMemo(() => buildMonthOptions(), []);

  const [admins, setAdmins] = useState<AdministrativePerson[]>([]);
  const [selectedPersonId, setSelectedPersonId] = useState<number | "">("");
  const [selectedPositionId, setSelectedPositionId] = useState<number | "">("");
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0]?.value ?? "");
  const [selectedWeek, setSelectedWeek] = useState<number>(0);
  // Vista del resumen: mes completo o una quincena específica (q1 = días 1-15,
  // q2 = días 16-fin). Cada quincena calcula con SUS propias horas extra/deducciones.
  const [summaryView, setSummaryView] = useState<"monthly" | "q1" | "q2">("monthly");
  const [calc, setCalc] = useState<AdministrativeSalaryCalculation | null>(null);
  const [days, setDays] = useState<AdministrativeSalaryDay[]>([]);
  // Salario por hora editable: arranca en 0 para períodos nuevos y se llena
  // con el snapshot persistido en períodos ya cerrados (read-only).
  // sphText es el buffer de edición libre (permite borrar todo o escribir "."),
  // se sincroniza con salaryPerHour al cargar y se "commitea" en blur.
  const [salaryPerHour, setSalaryPerHour] = useState<number>(0);
  const [sphText, setSphText] = useState<string>("0");
  const [loadingCalc, setLoadingCalc] = useState(false);
  const [saving, setSaving] = useState(false);
  // Acción pendiente de confirmar: "save" guarda avances (el período sigue
  // editable), "close" cierra el control (solo lectura definitivo).
  const [confirmAction, setConfirmAction] = useState<"save" | "close" | null>(null);
  const [excessAlert, setExcessAlert] = useState<{
    dayLabel: string;
    baseHours: number;
    extraHours: number;
    vacationHours: number;
    deductionHours: number;
    vacDedExceedsBase: boolean;
    baseExtraExceeds24: boolean;
  } | null>(null);

  // Cargar lista de administrativos al inicio
  useEffect(() => {
    (async () => {
      try {
        const list = await getAdministrativePersons();
        setAdmins(list);
        if (list.length > 0 && selectedPersonId === "") {
          const first = list[0];
          setSelectedPersonId(first.personId);
          setSelectedPositionId(first.positions?.[0]?.positionId ?? "");
        }
      } catch (err: any) {
        setErrorMsg?.(
          err?.message ?? "No se pudo cargar la lista de colaboradores"
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cargo seleccionado: opciones del colaborador actual
  const selectedPerson = useMemo(
    () => admins.find((a) => a.personId === selectedPersonId) ?? null,
    [admins, selectedPersonId]
  );
  const positionOptions = selectedPerson?.positions ?? [];

  // Si el colaborador cambia y el cargo actual no pertenece a sus opciones,
  // resetear al primer cargo disponible.
  useEffect(() => {
    if (!selectedPerson) {
      setSelectedPositionId("");
      return;
    }
    const stillValid = selectedPerson.positions?.some(
      (p) => p.positionId === selectedPositionId
    );
    if (!stillValid) {
      setSelectedPositionId(selectedPerson.positions?.[0]?.positionId ?? "");
    }
  }, [selectedPerson, selectedPositionId]);

  const loadCalculation = useCallback(async () => {
    if (!selectedPersonId || !selectedMonth) return;
    const opt = monthOptions.find((o) => o.value === selectedMonth);
    if (!opt) return;
    setLoadingCalc(true);
    try {
      const data = await getAdministrativeSalaryCalculation(
        Number(selectedPersonId),
        opt.year,
        opt.month,
        undefined,
        selectedPositionId === "" ? undefined : Number(selectedPositionId)
      );
      setCalc(data);
      setDays(data.days);
      const sph = data.salaryPerHour ?? 0;
      setSalaryPerHour(sph);
      setSphText(String(sph));
      // Si el back devolvió otro positionId (período ya guardado lo fija al cargo persistido),
      // sincronizar la UI con el cargo efectivo.
      if (data.positionId && data.positionId !== selectedPositionId) {
        setSelectedPositionId(data.positionId);
      }
    } catch (err: any) {
      setCalc(null);
      setDays([]);
      setErrorMsg?.(err?.message ?? "No se pudo cargar el cálculo");
    } finally {
      setLoadingCalc(false);
    }
  }, [selectedPersonId, selectedPositionId, selectedMonth, monthOptions, setErrorMsg]);

  useEffect(() => {
    loadCalculation();
  }, [loadCalculation]);


  const formatDayLabel = (dayDate: string) => {
    const date = new Date(dayDate + "T00:00:00");
    return `${WEEK_DAY_LABELS[date.getDay()]} ${String(date.getDate()).padStart(2, "0")}`;
  };

  const isDayInvalid = (d: AdministrativeSalaryDay) => {
    // Un día de vacaciones va con horas base en 0 y la jornada en horas de
    // vacaciones, así que "vacaciones + deducciones > base" siempre se cumple.
    // No es un error: es exactamente como debe quedar.
    if (d.vacation) return false;
    const base = Number(d.baseHours) || 0;
    const extra = Number(d.extraHours) || 0;
    const vac = Number(d.vacationHours) || 0;
    const ded = Number(d.deductionHours) || 0;
    return vac + ded > base || base + extra > 24;
  };

  const handleDayChange = (
    dayDate: string,
    field: keyof AdministrativeSalaryDay,
    raw: string
  ) => {
    const num = raw === "" ? 0 : Number(raw);
    if (Number.isNaN(num) || num < 0) return;
    setDays((prev) =>
      prev.map((d) => {
        if (d.dayDate !== dayDate) return d;
        const updated = { ...d, [field]: num } as AdministrativeSalaryDay;
        const base = Number(updated.baseHours) || 0;
        const extra = Number(updated.extraHours) || 0;
        const vac = Number(updated.vacationHours) || 0;
        const ded = Number(updated.deductionHours) || 0;
        // En un día de vacaciones las horas base son 0 a propósito, así que la
        // comparación contra la base no aplica (ver isDayInvalid).
        const vacDedExceedsBase = !updated.vacation && vac + ded > base;
        const baseExtraExceeds24 = base + extra > 24;
        if (vacDedExceedsBase || baseExtraExceeds24) {
          setExcessAlert({
            dayLabel: formatDayLabel(d.dayDate),
            baseHours: base,
            extraHours: extra,
            vacationHours: vac,
            deductionHours: ded,
            vacDedExceedsBase,
            baseExtraExceeds24,
          });
        }
        return updated;
      })
    );
  };

  const handleSave = (action: "save" | "close") => {
    if (!selectedPersonId || !selectedMonth) return;
    setConfirmAction(action);
  };

  const handleConfirmSave = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (!action) return;
    const opt = monthOptions.find((o) => o.value === selectedMonth);
    if (!opt) return;
    setSaving(true);
    setLoading?.(true);
    if (selectedPositionId === "") {
      setErrorMsg?.("Debe seleccionar un cargo antes de guardar");
      setSaving(false);
      setLoading?.(false);
      return;
    }
    try {
      await saveAdministrativeSalary({
        personId: Number(selectedPersonId),
        positionId: Number(selectedPositionId),
        year: opt.year,
        month: opt.month,
        user: authInfo?.username ?? "",
        salaryPerHour,
        days,
        close: action === "close",
      });
      setSuccessMsg?.(
        action === "close"
          ? "Control de salario cerrado correctamente"
          : "Cálculo del salario guardado correctamente"
      );
      await loadCalculation();
    } catch (err: any) {
      setErrorMsg?.(err?.message ?? "Error al guardar");
    } finally {
      setSaving(false);
      setLoading?.(false);
    }
  };

  const handleCancel = () => {
    loadCalculation();
  };

  const isClosed = calc?.closed === true;

  const monthOption = monthOptions.find((m) => m.value === selectedMonth);
  const monthName = monthOption ? MONTH_LABELS[monthOption.month - 1] : "";

  // Valores especiales del filtro de período:
  //   0  → mes completo
  //  -1  → primera quincena (días 1-15)
  //  -2  → segunda quincena (días 16-fin)
  //  >0  → semana N
  const weekOptions = useMemo(() => {
    const base = [
      { value: 0, label: "Mensual" },
      { value: -1, label: "Primera quincena" },
      { value: -2, label: "Segunda quincena" },
    ];
    if (!monthOption) return base;
    const count = countWeeksInMonth(monthOption.year, monthOption.month);
    for (let i = 1; i <= count; i++) {
      base.push({ value: i, label: `Semana ${i}` });
    }
    return base;
  }, [monthOption]);

  // Si el mes cambia y la semana seleccionada ya no existe, resetear a "Mensual"
  useEffect(() => {
    if (!monthOption) return;
    const count = countWeeksInMonth(monthOption.year, monthOption.month);
    if (selectedWeek > count) setSelectedWeek(0);
  }, [monthOption, selectedWeek]);

  const displayedDays = useMemo(() => {
    if (selectedWeek === 0) return days;
    if (selectedWeek === -1) {
      return days.filter((d) => new Date(d.dayDate + "T00:00:00").getDate() <= 15);
    }
    if (selectedWeek === -2) {
      return days.filter((d) => new Date(d.dayDate + "T00:00:00").getDate() >= 16);
    }
    return days.filter((d) => getWeekPositionInMonth(d.dayDate) === selectedWeek);
  }, [days, selectedWeek]);

  const periodLabel = useMemo(
    () => weekOptions.find((w) => w.value === selectedWeek)?.label ?? "Mensual",
    [weekOptions, selectedWeek]
  );

  const sectionTitle =
    selectedWeek === 0 ? `${monthName}` : `${monthName} - ${periodLabel}`;

  // Cálculos visuales del resumen según toggle
  // Recalcula en vivo cuando el usuario edita extras/deducciones:
  //   bruto_base       = salary_per_hour × person.baseHours × 30  (fijo teórico, viene del back)
  //   extras           = Σ (horas_extra × salary_per_hour × 1.5  ó  ×2 si feriado)
  //   deducciones      = Σ (horas_deducción × salary_per_hour)
  //   bruto            = bruto_base + extras   (completo: las deducciones NO lo restan)
  //   ccss             = (bruto − deducciones) × ccssTaxPercent / 100
  //   neto             = bruto − deducciones − ccss
  // Las horas base por día de la tabla son informativas y NO suman al bruto.
  const CCSS_TAX_PCT = 10.83; // mismo % que devuelve get_ccss_tax() actualmente

  const summary = useMemo(() => {
    if (!calc) return undefined;
    const sph = salaryPerHour;
    // Mensual: todos los días. Quincenal: solo los días de la quincena elegida,
    // para que cada quincena refleje sus propias horas extra/deducciones.
    const summaryDays =
      summaryView === "monthly"
        ? days
        : days.filter((d) => {
            const dayNum = new Date(d.dayDate + "T00:00:00").getDate();
            return summaryView === "q1" ? dayNum <= 15 : dayNum >= 16;
          });
    let totalExtraAmount = 0;
    let totalDeductionAmount = 0;
    let totalExtraHours = 0;
    let totalDeductionHours = 0;
    let workedDays = 0;
    summaryDays.forEach((d) => {
      const base = Number(d.baseHours) || 0;
      const extra = Number(d.extraHours) || 0;
      const ded = Number(d.deductionHours) || 0;
      const multiplier = d.holiday ? 2 : 1.5;
      totalExtraAmount += extra * sph * multiplier;
      totalExtraHours += extra;
      totalDeductionAmount += ded * sph;
      totalDeductionHours += ded;
      if (base > 0) workedDays++;
    });
    // Base teórica del período: 30 días para el mes, 15 por quincena
    const baseDays = summaryView === "monthly" ? 30 : 15;
    const periodBase = (calc.dailySalary ?? 0) * baseDays;
    // El bruto se muestra completo (base + extras); las deducciones solo
    // bajan la base imponible de CCSS y el neto.
    const grossCalculated = periodBase + totalExtraAmount;
    const ccssAmount =
      ((grossCalculated - totalDeductionAmount) * CCSS_TAX_PCT) / 100;
    const netSalary = grossCalculated - totalDeductionAmount - ccssAmount;
    return {
      workedDays,
      grossSalary: grossCalculated,
      extraHoursAmount: totalExtraAmount,
      extraHoursCount: totalExtraHours,
      deductionHoursAmount: totalDeductionAmount,
      deductionHoursCount: totalDeductionHours,
      ccssAmount,
      netSalary,
    };
  }, [calc, days, salaryPerHour, summaryView]);

  return (
    <FancyPaper
      pagetitle="Colaboradores / Cálculo salarios"
      prevpage="Colaboradores"
    >
      {/* Título sección Cálculo */}
      <Typography
        variant="h6"
        color="primary"
        sx={{ fontWeight: 700, mb: 2, fontSize: "1.4rem" }}
      >
        Cálculo
      </Typography>

      {/* Filtros: Colaborador | Cargo | Mes | Semana */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3.5}>
          <Autocomplete
            fullWidth
            size="small"
            options={admins}
            getOptionLabel={(opt) => opt.fullName}
            value={admins.find((a) => a.personId === selectedPersonId) ?? null}
            onChange={(_, v) => {
              // Setear ambos en el mismo tick para evitar que loadCalculation
              // dispare con (nuevo personId, viejo positionId).
              if (v) {
                setSelectedPersonId(v.personId);
                setSelectedPositionId(v.positions?.[0]?.positionId ?? "");
              } else {
                setSelectedPersonId("");
                setSelectedPositionId("");
              }
            }}
            isOptionEqualToValue={(opt, val) => opt.personId === val.personId}
            noOptionsText="Sin resultados"
            sx={{
              background: "#FFF",
              "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
                padding: "0 9px",
              },
              "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input": {
                padding: "4px 4px 5px 4px",
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Colaborador"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.5}>
          <TextField
            select
            fullWidth
            size="small"
            label="Cargo"
            value={selectedPositionId === "" ? "" : String(selectedPositionId)}
            onChange={(e) =>
              setSelectedPositionId(
                e.target.value === "" ? "" : Number(e.target.value)
              )
            }
            disabled={positionOptions.length === 0}
            InputLabelProps={{ shrink: true }}
            sx={{ background: "#FFF" }}
          >
            {positionOptions.length === 0 && (
              <MenuItem value="" disabled>
                Sin cargo
              </MenuItem>
            )}
            {positionOptions.map((p) => (
              <MenuItem key={p.positionId} value={String(p.positionId)}>
                {p.positionName}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Mes"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ background: "#FFF" }}
          >
            {monthOptions.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="Período"
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(Number(e.target.value))}
            InputLabelProps={{ shrink: true }}
            sx={{ background: "#FFF" }}
          >
            {weekOptions.map((w) => (
              <MenuItem key={w.value} value={w.value}>
                {w.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {/* Tarjetas del período en curso (live).
          Las primeras 5 reflejan lo que se está editando; las últimas 3 son
          tasas constantes del cargo. */}
      <Box sx={{ height: 48 }} />
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        {(() => {
          // Tarjetas teóricas fijas — no se afectan por horas extras/deducciones
          // (esos efectos viven solo en "Resumen de Cálculo" más abajo).
          const sph = salaryPerHour;
          const grossMonthly = calc?.grossMonthlySalary ?? 0;
          const netMonthly = calc?.netMonthlySalary ?? 0;
          const grossBiweekly = calc?.grossBiweeklySalary ?? 0;
          const netBiweekly = calc?.netBiweeklySalary ?? 0;
          const dailyGross = calc?.dailySalary ?? 0;
          return [
            { label: "Salario bruto del período", value: grossMonthly },
            { label: "Salario neto del período", value: netMonthly },
            { label: "Salario bruto quincenal", value: grossBiweekly },
            { label: "Salario neto quincenal", value: netBiweekly },
            { label: "Salario diario", value: dailyGross },
            { label: "Salario bruto por hora", value: sph },
            { label: "Salario neto por hora", value: sph * (1 - CCSS_TAX_PCT / 100) },
            { label: "Salario por hora extra (1.5x)", value: sph * 1.5 },
          ];
        })().map((card) => (
          <Grid key={card.label} item xs={12} sm={6} md={1.5}>
            <Stack spacing={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.1 }}
              >
                {card.label}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  background: "#FFF",
                  minHeight: 38,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "default",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "center" }}>
                  {formatCRC(card.value ?? 0)}
                </Typography>
              </Box>
            </Stack>
          </Grid>
        ))}
      </Grid>

      {/* Header de la tabla con "Mes - Semana N" + botón Ver historial */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mt: 3, mb: 3 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 700, fontSize: "1.4rem" }}>
            {sectionTitle}
          </Typography>
          {isClosed && (
            <Chip
              icon={<LockIcon sx={{ fontSize: 16 }} />}
              label="Período cerrado — solo lectura"
              size="small"
              sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 600, borderRadius: "8px" }}
            />
          )}
        </Stack>
        <Button
          variant="contained"
          color="primary"
          sx={{ borderRadius: "20px", textTransform: "none", px: 3, my: 1 }}
          onClick={() => navigate("/dashboard/salaryHistory")}
        >
          Ver historial de salarios
        </Button>
      </Stack>
      <Divider sx={{ mt: 6, mb: 6, borderBottomWidth: 2, borderColor: "#000" }} />

      {loadingCalc ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            mb: 3,
            boxShadow: "none",
            background: "transparent",
            border: "1px solid #E5D9D2",
            overflow: "hidden",
          }}
        >
          <Table
            sx={{
              borderCollapse: "collapse !important",
              borderSpacing: "0 !important",
              margin: "0 !important",
              "& .MuiTableCell-head": {
                borderRadius: "0 !important",
                background: "#FFF !important",
              },
              "& .MuiTableCell-root": {
                border: "0 !important",
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={cellHeadSx}>Día</TableCell>
                <TableCell align="center" sx={cellHeadSx}>
                  Horas base
                </TableCell>
                <TableCell align="center" sx={cellHeadSx}>
                  Horas extra
                </TableCell>
                <TableCell align="center" sx={cellHeadSx}>
                  Horas vacaciones
                </TableCell>
                <TableCell align="center" sx={cellHeadSx}>
                  Horas deducciones
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedDays.map((d, idx) => {
                const date = new Date(d.dayDate + "T00:00:00");
                const dayName = WEEK_DAY_LABELS[date.getDay()];
                const dayNum = String(date.getDate()).padStart(2, "0");
                const firstRowPt = idx === 0 ? 2.5 : 1;
                const invalid = isDayInvalid(d);
                const onVacation = !!d.vacation;
                return (
                  <TableRow
                    key={d.dayDate}
                    sx={{
                      "& td": { borderBottom: 0, pt: firstRowPt },
                      ...(onVacation
                        ? { backgroundColor: "rgba(2, 136, 209, 0.10)" }
                        : {}),
                    }}
                  >
                    <TableCell
                      sx={{
                        color: onVacation ? VACATION_COLOR : "#6B6B6B",
                        fontWeight: onVacation ? 600 : "inherit",
                        fontSize: 14,
                        py: 1,
                        pl: 3,
                      }}
                    >
                      {onVacation ? (
                        <Tooltip
                          placement="top"
                          title="Vacaciones: las horas base van en 0 y la jornada se paga como horas de vacaciones"
                        >
                          <span>
                            {dayName} {dayNum} · Vacaciones
                          </span>
                        </Tooltip>
                      ) : (
                        <>
                          {dayName} {dayNum}
                        </>
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <PillInput
                        value={d.baseHours}
                        onChange={(v) => handleDayChange(d.dayDate, "baseHours", v)}
                        disabled={isClosed || onVacation}
                        error={invalid}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <PillInput
                        value={d.extraHours}
                        onChange={(v) => handleDayChange(d.dayDate, "extraHours", v)}
                        disabled={isClosed}
                        error={invalid}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <PillInput
                        value={d.vacationHours}
                        onChange={(v) => handleDayChange(d.dayDate, "vacationHours", v)}
                        disabled
                        error={invalid}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <PillInput
                        value={d.deductionHours}
                        onChange={(v) => handleDayChange(d.dayDate, "deductionHours", v)}
                        disabled={isClosed}
                        error={invalid}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {displayedDays.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No hay días para mostrar.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Resumen */}
      <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 1, fontSize: "1.4rem" }}>
        Resumen de Cálculo
      </Typography>
      <Divider sx={{ mt: 6, mb: 6, borderBottomWidth: 2, borderColor: "#000" }} />
      <Paper
        sx={{
          py: 3,
          px: 16,
          borderRadius: 2,
          background: "#FFF",
          border: "1px solid #E0E0E0",
          mb: 3,
        }}
        elevation={0}
      >
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5 }}
          >
            Vista del resumen
          </Typography>
          <RadioGroup
            row
            value={summaryView}
            onChange={(e) =>
              setSummaryView(e.target.value as "monthly" | "q1" | "q2")
            }
          >
            <FormControlLabel
              value="monthly"
              control={
                <Radio
                  size="small"
                  icon={<RadioButtonUncheckedIcon sx={{ color: "#BDBDBD" }} />}
                  checkedIcon={<CheckCircleIcon sx={{ color: "#1A932E" }} />}
                />
              }
              label="Mensual"
            />
            <FormControlLabel
              value="q1"
              control={
                <Radio
                  size="small"
                  icon={<RadioButtonUncheckedIcon sx={{ color: "#BDBDBD" }} />}
                  checkedIcon={<CheckCircleIcon sx={{ color: "#1A932E" }} />}
                />
              }
              label="Primera quincena"
            />
            <FormControlLabel
              value="q2"
              control={
                <Radio
                  size="small"
                  icon={<RadioButtonUncheckedIcon sx={{ color: "#BDBDBD" }} />}
                  checkedIcon={<CheckCircleIcon sx={{ color: "#1A932E" }} />}
                />
              }
              label="Segunda quincena"
            />
          </RadioGroup>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={6}>
            <Stack>
              <Row
                label="Días trabajados"
                value={summary?.workedDays ?? 0}
              />
              <Row
                label={
                  summaryView === "monthly"
                    ? "Salario mensual bruto"
                    : "Salario quincenal bruto"
                }
                value={formatCRC(summary?.grossSalary ?? 0)}
              />
              <Row
                label="Cantidad horas extra"
                value={formatHours(summary?.extraHoursCount ?? 0)}
              />
              <Row
                label="Monto horas extra"
                value={formatCRC(summary?.extraHoursAmount ?? 0)}
                last
              />
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Stack>
              <Row
                label="Cantidad horas a deducir"
                value={formatHours(summary?.deductionHoursCount ?? 0)}
              />
              <Row
                label="Monto de deducciones"
                value={formatCRC(summary?.deductionHoursAmount ?? 0)}
              />
              <Row
                label="CCSS"
                value={formatCRC(summary?.ccssAmount ?? 0)}
              />
              <Row
                label={
                  summaryView === "monthly"
                    ? "Salario mensual neto"
                    : "Salario quincenal neto"
                }
                value={formatCRC(summary?.netSalary ?? 0)}
                emphasized
                last
              />
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Acciones */}
      {!isClosed && (
        <Stack
          direction="row"
          spacing={2}
          justifyContent="flex-end"
          sx={{ mb: 2 }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={handleCancel}
            disabled={saving}
            sx={{ borderRadius: "20px", textTransform: "none", px: 4 }}
          >
            Cancelar
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleSave("save")}
            disabled={saving || !selectedPersonId}
            sx={{ borderRadius: "20px", textTransform: "none", px: 4 }}
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSave("close")}
            disabled={saving || !selectedPersonId}
            sx={{ borderRadius: "20px", textTransform: "none", px: 4 }}
          >
            Cerrar control
          </Button>
        </Stack>
      )}

      {/* Modal informativo: horas del día inválidas */}
      <Dialog
        open={excessAlert !== null}
        onClose={() => setExcessAlert(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#D32F2F" }}>
          Horas inválidas
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            Día <strong>{excessAlert?.dayLabel}</strong>:
            {excessAlert?.vacDedExceedsBase && (
              <Box sx={{ mt: 1 }}>
                · La suma de <strong>vacaciones</strong> (
                {excessAlert.vacationHours}h) y <strong>deducciones</strong> (
                {excessAlert.deductionHours}h) es{" "}
                <strong>
                  {excessAlert.vacationHours + excessAlert.deductionHours}h
                </strong>{" "}
                y no puede superar las <strong>horas base</strong> (
                {excessAlert.baseHours}h).
              </Box>
            )}
            {excessAlert?.baseExtraExceeds24 && (
              <Box sx={{ mt: 1 }}>
                · La suma de <strong>horas base</strong> (
                {excessAlert.baseHours}h) y <strong>horas extra</strong> (
                {excessAlert.extraHours}h) es{" "}
                <strong>
                  {excessAlert.baseHours + excessAlert.extraHours}h
                </strong>{" "}
                y no puede superar las <strong>24h</strong> del día.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setExcessAlert(null)}
            variant="contained"
            color="primary"
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación (guardar avances / cerrar control) */}
      <Dialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {confirmAction === "close" ? "Confirmar cierre del control" : "Confirmar guardado"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmAction === "close" ? (
              <>
                ¿Está seguro de cerrar el control de salario para{" "}
                <strong>{calc?.personName}</strong> —{" "}
                <strong>{calc?.positionName}</strong>, período{" "}
                <strong>{monthName} {monthOption?.year}</strong>?
                <br /><br />
                Una vez cerrado, el período quedará en solo lectura y no podrá
                modificarse.
              </>
            ) : (
              <>
                ¿Está seguro de guardar el cálculo de salario para{" "}
                <strong>{calc?.personName}</strong> —{" "}
                <strong>{calc?.positionName}</strong>, período{" "}
                <strong>{monthName} {monthOption?.year}</strong>?
                <br /><br />
                Podrá seguir editando los datos hasta que cierre el control.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmAction(null)}
            variant="outlined"
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmSave}
            variant="contained"
            color="primary"
            sx={{ borderRadius: "20px", textTransform: "none" }}
          >
            {confirmAction === "close" ? "Sí, cerrar control" : "Sí, guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {!loadingCalc && admins.length === 0 && (
        <Alert severity="warning">
          No hay colaboradores con un cargo administrativo (payroll_exclude =
          true). Configure cargos administrativos en Configuración → Posiciones.
        </Alert>
      )}
    </FancyPaper>
  );
};

const Row = ({
  label,
  value,
  emphasized,
  last,
}: {
  label: string;
  value: string | number;
  emphasized?: boolean;
  last?: boolean;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={3}
    sx={{
      py: 2.2,
      borderBottom: last ? "none" : "1px solid #EEE",
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 200 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: emphasized ? 800 : 600,
        color: emphasized ? "#1B5E20" : undefined,
      }}
    >
      {value}
    </Typography>
  </Stack>
);

export default ManagementSalaryCalculation;
